import { supabase, TABLES } from '../config/supabase';
import { z } from 'zod';

/**
 * Standardized error object structure
 * @typedef {Object} ServiceError
 * @property {boolean} success - Always false for errors
 * @property {string} message - User-friendly error message
 * @property {string} code - Error code for programmatic handling
 * @property {*} details - Additional error details
 */

/**
 * Standardized success response structure
 * @typedef {Object} ServiceResponse
 * @property {boolean} success - Always true for success
 * @property {*} data - Response data
 * @property {string} [message] - Optional success message
 */

// Zod Schema for Account Validation
const accountSchema = z.object({
  name: z.string().min(1, 'Account name is required'),
  industry: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  // Validate URLs or allow empty strings/null
  website: z.string().url('Invalid website URL').optional().or(z.literal('')).nullable(),
  linkedin_url: z.string().url('Invalid LinkedIn URL').optional().or(z.literal('')).nullable(),
  tax_id: z.string().optional().nullable(),
  notes: z.string().optional().nullable()
});

/**
 * Creates a standardized error object
 */
const createError = (message, code = 'UNKNOWN_ERROR', details = null) => ({
  success: false,
  message,
  code,
  details
});

/**
 * Creates a standardized success response
 */
const createSuccess = (data, message = null) => ({
  success: true,
  data,
  ...(message && { message })
});

/**
 * Parses Supabase errors
 */
const parseSupabaseError = (error, operation) => {
  if (!error) {
    return createError(`Unknown error during ${operation}`, 'UNKNOWN_ERROR');
  }

  const errorMap = {
    'PGRST116': { message: 'Account not found', code: 'NOT_FOUND' },
    '23505': { message: 'An account with this name already exists', code: 'DUPLICATE' },
    '23503': { message: 'Related record not found', code: 'FOREIGN_KEY_VIOLATION' },
    '42501': { message: 'Permission denied', code: 'PERMISSION_DENIED' }
  };

  const mappedError = errorMap[error.code];
  if (mappedError) {
    return createError(mappedError.message, mappedError.code, { originalError: error });
  }

  return createError(
    error.message || `Failed to ${operation}`,
    error.code || 'DATABASE_ERROR',
    { originalError: error }
  );
};

class AccountService {
  constructor() {
    this.tableName = TABLES.ACCOUNTS;
  }

  /**
   * Fetches all accounts (excluding soft-deleted ones)
   */
  async getAccounts(options = {}) {
    try {
      const { select = '*', filters = {}, orderBy = 'name', ascending = true, limit = null } = options;

      let query = supabase
        .from(this.tableName)
        .select(select)
        .is('deleted_at', null); // Filter out soft-deleted records

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query = query.eq(key, value);
        }
      });

      if (orderBy) query = query.order(orderBy, { ascending });
      if (limit && limit > 0) query = query.limit(limit);

      const { data, error } = await query;

      if (error) return parseSupabaseError(error, 'fetch accounts');

      return createSuccess(data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
    }
  }

  /**
   * Creates a new account with Zod validation
   */
  async createAccount(data) {
    try {
      if (!data) return createError('Account data is required', 'INVALID_INPUT');

      // Check authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return createError('User not authenticated', 'UNAUTHORIZED');

      // Validate with Zod
      const validationResult = accountSchema.safeParse(data);

      if (!validationResult.success) {
        const errorMessage = validationResult.error.errors[0]?.message || 'Validation failed';
        return createError(errorMessage, 'VALIDATION_ERROR', { errors: validationResult.error.errors });
      }

      const validData = validationResult.data;

      // Clean empty strings to null for database consistency if preferred, 
      // or keep as empty strings. Let's sanitize to null for cleaner DB.
      const sanitizedData = {
        name: validData.name.trim(),
        industry: validData.industry?.trim() || null,
        city: validData.city?.trim() || null,
        country: validData.country?.trim() || null,
        website: validData.website?.trim() || null,
        linkedin_url: validData.linkedin_url?.trim() || null,
        tax_id: validData.tax_id?.trim() || null,
        notes: validData.notes?.trim() || null
      };

      const { data: result, error } = await supabase
        .from(this.tableName)
        .insert(sanitizedData)
        .select()
        .single();

      if (error) return parseSupabaseError(error, 'create account');

      return createSuccess(result, 'Account created successfully');
    } catch (error) {
      console.error('Error creating account:', error);
      return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
    }
  }

  /**
   * Updates an existing account with Zod validation (Partial Update)
   */
  async updateAccount(id, data) {
    try {
      if (!id) return createError('Account ID is required', 'INVALID_INPUT');
      if (!data || Object.keys(data).length === 0) return createError('Update data is required', 'INVALID_INPUT');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return createError('User not authenticated', 'UNAUTHORIZED');

      // Create a partial schema for updates
      const partialSchema = accountSchema.partial();
      const validationResult = partialSchema.safeParse(data);

      if (!validationResult.success) {
        const errorMessage = validationResult.error.errors[0]?.message || 'Validation failed';
        return createError(errorMessage, 'VALIDATION_ERROR', { errors: validationResult.error.errors });
      }

      // Clean data
      const updateData = Object.entries(validationResult.data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = typeof value === 'string' ? (value.trim() || null) : value;
        }
        return acc;
      }, {});

      const { data: result, error } = await supabase
        .from(this.tableName)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) return parseSupabaseError(error, 'update account');

      return createSuccess(result, 'Account updated successfully');
    } catch (error) {
      console.error('Error updating account:', error);
      return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
    }
  }

  /**
   * Soft deletes an account by setting deleted_at timestamp
   */
  async deleteAccount(id) {
    try {
      if (!id) return createError('Account ID is required', 'INVALID_INPUT');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return createError('User not authenticated', 'UNAUTHORIZED');

      // Soft delete: Update deleted_at instead of DELETE
      const { data: result, error } = await supabase
        .from(this.tableName)
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) return parseSupabaseError(error, 'delete account');
      if (!result) return createError('Account not found', 'NOT_FOUND');

      return createSuccess(null, 'Account deleted successfully');
    } catch (error) {
      console.error('Error deleting account:', error);
      return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
    }
  }

  /**
   * Helper methods (getAccountById, searchAccounts, etc.)
   * Reuse existing implementations but ensure they filter deleted_at
   */

  async getAccountById(id) {
    try {
      if (!id) return createError('Account ID is required', 'INVALID_INPUT');

      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .single();

      if (error) return parseSupabaseError(error, 'fetch account');
      return createSuccess(data);
    } catch (error) {
      return createError('Error fetching account', 'UNEXPECTED_ERROR', { error });
    }
  }

  async searchAccounts(searchTerm, options = {}) {
    try {
      if (!searchTerm?.trim()) return createError('Search term required', 'INVALID_INPUT');
      const { limit = 50 } = options;

      // Avoid using .or() with string interpolation as it breaks with special characters (commas, periods, etc.)
      // Instead, use separate queries and merge results
      const searchPattern = `%${searchTerm}%`;

      // Search by name
      const { data: nameMatches, error: nameError } = await supabase
        .from(this.tableName)
        .select('*')
        .ilike('name', searchPattern)
        .is('deleted_at', null)
        .order('name', { ascending: true })
        .limit(limit);

      if (nameError) return parseSupabaseError(nameError, 'search accounts by name');

      // Search by industry
      const { data: industryMatches, error: industryError } = await supabase
        .from(this.tableName)
        .select('*')
        .ilike('industry', searchPattern)
        .is('deleted_at', null)
        .order('name', { ascending: true })
        .limit(limit);

      if (industryError) return parseSupabaseError(industryError, 'search accounts by industry');

      // Merge and deduplicate results
      const combinedData = [...(nameMatches || [])];
      const existingIds = new Set((nameMatches || []).map(a => a.id));

      (industryMatches || []).forEach(account => {
        if (!existingIds.has(account.id)) {
          combinedData.push(account);
        }
      });

      // Sort by name and limit
      const sortedData = combinedData
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, limit);

      return createSuccess(sortedData);
    } catch (error) {
      return createError('Error searching accounts', 'UNEXPECTED_ERROR', { error });
    }
  }

  async getAccountStats(accountId) {
    try {
      if (!accountId) return createError('Account ID required', 'INVALID_INPUT');

      const { data: account, error } = await supabase
        .from(this.tableName)
        .select(`
          *,
          contacts(id),
          opportunities(id, stage, amount)
        `)
        .eq('id', accountId)
        .is('deleted_at', null)
        .single();

      if (error) return parseSupabaseError(error, 'fetch account details');

      const stats = {
        totalContacts: account.contacts?.length || 0,
        totalOpportunities: account.opportunities?.length || 0,
        activeOpportunities: account.opportunities?.filter(o => !['Closed Won', 'Closed Lost'].includes(o.stage)).length || 0,
        wonOpportunities: account.opportunities?.filter(o => o.stage === 'Closed Won').length || 0,
        totalValue: account.opportunities?.reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0) || 0
      };

      return createSuccess({ account, stats });
    } catch (error) {
      return createError('Error fetching stats', 'UNEXPECTED_ERROR', { error });
    }
  }
}

export const accountService = new AccountService();